import React, { useState, useMemo } from "react";
import { GroupedData, IncidentQueryField } from "../types/salesforce";
import DataTable from "./DataTable";
import Cell from "./Cell";

interface GroupedResultsProps {
  groupedData: GroupedData[];
  selectedFields: string[];
  availableFields: IncidentQueryField[];
  level1Field: string;
  level2Field?: string;
}

export const GroupedResults: React.FC<GroupedResultsProps> = ({
  groupedData,
  selectedFields,
  availableFields,
  level1Field,
  level2Field,
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const getFieldLabel = (fieldName: string) => {
    return (
      availableFields.find((f) => f.Field_API_Name__c === fieldName)?.Label ||
      fieldName
    );
  };

  // Define columns using selectedFields and availableFields
  const columns = useMemo(() => {
    return selectedFields.map((field) => {
      const fieldMeta = availableFields.find(
        (f) => f.Field_API_Name__c === field
      );
      const fieldType = fieldMeta?.Field_Type__c || "Text";
      const Header = fieldMeta?.Label || field;

      return {
        Header,
        accessor: field,
        // @ts-ignore
        Cell: (props: { value: any }) => (
          <Cell value={props.value} fieldType={fieldType} />
        ),
      };
    });
  }, [selectedFields, availableFields]);

  return (
    <div className="space-y-6">
      {groupedData.map((group, groupIndex) => {
        const groupId = `${level1Field}-${group.groupValue}-${groupIndex}`;
        const isExpanded = expandedGroups.has(groupId);

        return (
          <div
            key={groupId}
            className="bg-white overflow-hidden rounded-xl text-white-main shadow-sm transition-all hover:shadow-md"
          >
            <button
              className="w-full cursor-pointer border-0 p-6 text-left text-white-main hover:bg-purple-600 focus:outline-none"
              onClick={() => toggleGroup(groupId)}
              style={{ backgroundColor: "#3D0052" }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleGroup(groupId);
                }
              }}
              aria-expanded={isExpanded}
              aria-controls={`group-content-${groupId}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span
                    className="text-white-main transition-transform duration-200"
                    style={{
                      transform: `rotate(${isExpanded ? "90deg" : "0deg"})`,
                    }}
                    aria-hidden="true"
                  >
                    ▶
                  </span>
                  <h3
                    id={`group-header-${groupId}`}
                    className="text-xl font-medium text-white-main"
                  >
                    {getFieldLabel(level1Field)}
                    {": "}
                    <span className="text-white-main">
                      {group.groupValue !== "Undefined" ? group.groupValue : ""}
                    </span>
                  </h3>
                </div>
                <span className="rounded-full bg-purple-100 px-4 py-1 text-sm font-medium text-purple-700">
                  {group.count} items
                </span>
              </div>
            </button>

            <section
              id={`group-content-${groupId}`}
              className={`transition-all duration-200 ${
                isExpanded ? "opacity-100" : "h-0 opacity-0"
              }`}
              aria-labelledby={`group-header-${groupId}`}
            >
              {group.subgroups ? (
                <div className="divide-y divide-gray-100">
                  {group.subgroups.map((subgroup, sgIndex) => (
                    <div
                      key={`${level1Field}-${group.groupValue}-${level2Field}-${subgroup.groupValue}-${sgIndex}`}
                      className="p-6"
                    >
                      <div className="mb-4 flex items-center justify-between">
                        <h4 className="text-lg font-medium text-gray-900">
                          {level2Field && getFieldLabel(level2Field)}{" "}
                          <span className="text-gray-500">
                            {subgroup.groupValue}
                          </span>
                        </h4>
                        <span className="rounded-full bg-purple-50 px-3 py-1 text-sm font-medium text-purple-700">
                          {subgroup.count} items
                        </span>
                      </div>
                      <DataTable
                        data={subgroup.records || []}
                        columns={columns}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6">
                  <DataTable data={group.records || []} columns={columns} />
                </div>
              )}
            </section>
          </div>
        );
      })}
    </div>
  );
};

export default GroupedResults;
